import { captureException } from '@sentry/react';

import DrawingTestInput from '~/modules/game-engine/input/drawing-test-input';
import dummyInput from '~/modules/game-engine/input/dummy-input';
import InputInterface from '~/modules/game-engine/input/interface';
import MicInput from '~/modules/game-engine/input/multi-mic-input';
import RemoteMicInput from '~/modules/game-engine/input/remote-mic-input';
import events from '~/modules/game-events/game-events';
import { PlayerNumber } from '~/modules/players/player-number';
import PlayersManager from '~/modules/players/players-manager';
import { DrawingTestInputSource } from '~/routes/select-input/input-sources/drawing-test';
import { InputSourceNames } from '~/routes/select-input/input-sources/interfaces';
import { MicrophoneInputSource } from '~/routes/select-input/input-sources/microphone';
import { RemoteMicrophoneInputSource } from '~/routes/select-input/input-sources/remote';

class InputManager {
  private isMonitoring = false;

  /**
   * Starting and stopping both open and close the same devices, and both are async - so run
   * concurrently they interleave, and the pipeline ends up in neither state. A stop that lands
   * mid-start closed the device the start had just opened and then dropped it from the device map,
   * which left `monitoringStarted()` reporting true with nothing open at all: the song heard silence
   * from the microphone the setup screen had just accepted.
   *
   * There is no shortage of overlapping callers - every screen with a level bar starts on mount and
   * stops on unmount, `changeInput` restarts, and `inputListChanged` restarts on top of that - so
   * they are queued rather than made safe one caller at a time. Each request runs to completion
   * before the next begins, which makes the last one asked for the state the pipeline is left in.
   */
  private queue: Promise<unknown> = Promise.resolve();

  private enqueue = <T>(task: () => Promise<T>) => {
    const result = this.queue.then(task, task);
    // The chain must survive a failed request - a device that refuses to open would otherwise
    // reject every later start and stop with its own error.
    this.queue = result.catch(() => undefined);

    return result;
  };

  constructor() {
    events.inputListChanged.subscribe(async (_initial, source) => {
      // Only the physical device list is a reason to reopen everything. A phone joining, dropping
      // off or being renamed changes nobody else's device, and this restart closes and reopens
      // every input the room is holding - mid-song that is a gap in the OTHER singers' microphones
      // caused by a phone that was never theirs. The remote mics do not need it either: assigning
      // one to a slot restarts through `changeInput`, and a phone coming back to a slot it already
      // held is started by the `remoteMicConnected` listener.
      if (source === RemoteMicrophoneInputSource.inputName) return;

      // Only this machine's devices are reopened: a phone's mic is not on that list, and closing
      // it mid-song costs that singer a second or more while the phone opens it again.
      if (this.isMonitoring) await this.restartLocalInputs();
    });
  }

  public getInputStatus = (playerNumber: PlayerNumber) => {
    const player = PlayersManager.getPlayer(playerNumber);
    if (!player) return 'unavailable';

    const source = this.sourceNameToInput(player.input.source);

    return source.getStatus(player.input.deviceId, player.input.channel);
  };

  public getPlayerFrequency = (playerNumber: PlayerNumber) => {
    const input = PlayersManager.getPlayer(playerNumber)?.input;
    // Player got removed
    if (!input) return 0;

    const frequencies = this.sourceNameToInput(input.source).getFrequencies(input.deviceId);

    return frequencies[input.channel];
  };

  public clearPlayerCachedFrequencies = (playerNumber: PlayerNumber) => {
    const input = PlayersManager.getPlayer(playerNumber)?.input;
    // Player got removed
    if (!input) return 0;

    this.sourceNameToInput(input.source).clearFrequencies(input.deviceId);
  };

  public getPlayerVolume = (playerNumber: PlayerNumber) => {
    const input = PlayersManager.getPlayer(playerNumber)?.input;
    if (!input) {
      return 0;
    }
    const volumes = this.sourceNameToInput(input.source).getVolumes(input.deviceId);

    return volumes[input.channel];
  };

  public getPlayerInputLag = (playerNumber: PlayerNumber) => {
    const input = PlayersManager.getPlayer(playerNumber)?.input;
    if (!input) {
      return 0;
    } else {
      return this.sourceNameToInput(input.source).getInputLag(input.deviceId);
    }
  };

  public startMonitoring = () => {
    // Set before awaiting the devices, not after. `monitoringStarted` is asked "is monitoring
    // engaged", and every caller uses it that way - to avoid starting twice, or to decide whether
    // to stop on unmount. Flipping it only once the last device has opened left a window, as long
    // as a `getUserMedia` call, where a restart triggered by changing a player's input reported
    // monitoring as off while it was coming back up.
    this.isMonitoring = true;

    // Read when the request is made, not when it runs. `changeInput` asks for a stop and then a
    // start around replacing a player's input, and each request has to act on the roster it was
    // made against: a stop that read the roster late would look for the microphone the slot holds
    // NOW and leave the one it was actually monitoring open - which is a phone still listening to a
    // singer it no longer belongs to.
    const allInputs = PlayersManager.getPlayers().map((player) => player.input);

    return this.enqueue(async () => {
      // allSettled, not all: a single unavailable device (unplugged mic, remote mic that dropped
      // off) must not reject out of here and leave the remaining players unmonitored.
      const results = await Promise.allSettled(
        allInputs.map((input) => this.sourceNameToInput(input.source).startMonitoring(input.deviceId, allInputs)),
      );
      results.forEach((result, index) => {
        if (result.status === 'rejected') {
          captureException(result.reason, {
            level: 'warning',
            extra: { message: 'InputManager.startMonitoring', input: allInputs[index] },
          });
        }
      });
    });
  };

  public stopMonitoring = () => {
    // Cleared up-front for the same reason, so the flag always describes the intent rather than
    // trailing it.
    this.isMonitoring = false;

    // Snapshot for the same reason as starting - what has to be closed is what this request was made
    // against, not whatever the roster holds by the time the queue reaches it.
    const allInputs = PlayersManager.getPlayers().map((player) => player.input);

    return this.enqueue(async () => {
      // allSettled for the same reason as starting, and for one more: `inputListChanged` restarts
      // monitoring by awaiting this and then starting again, so a device that fails to close would
      // otherwise reject out of here and the restart would never happen.
      const results = await Promise.allSettled(
        allInputs.map((input) => this.sourceNameToInput(input.source).stopMonitoring(input.deviceId)),
      );
      results.forEach((result) => {
        if (result.status === 'rejected') {
          captureException(result.reason, {
            level: 'warning',
            extra: { message: 'InputManager.stopMonitoring' },
          });
        }
      });
    });
  };

  private restartLocalInputs = () => {
    const allInputs = PlayersManager.getPlayers().map((player) => player.input);
    const local = allInputs.filter((input) => input.source !== RemoteMicrophoneInputSource.inputName);

    return this.enqueue(async () => {
      await Promise.allSettled(
        local.map((input) => this.sourceNameToInput(input.source).stopMonitoring(input.deviceId)),
      );
      const results = await Promise.allSettled(
        local.map((input) => this.sourceNameToInput(input.source).startMonitoring(input.deviceId, allInputs)),
      );
      results.forEach((result) => {
        if (result.status === 'rejected') {
          captureException(result.reason, { level: 'warning', extra: { message: 'InputManager.restartLocalInputs' } });
        }
      });
    });
  };

  public monitoringStarted = () => this.isMonitoring;

  // todo: Create eg. "InputSourceManager" and have the logic there?
  public sourceNameToInput = (sourceName: InputSourceNames): InputInterface => {
    if (sourceName === MicrophoneInputSource.inputName) return MicInput;
    // @ts-expect-error only-for-testing input not included in the typings
    if (sourceName === DrawingTestInputSource.inputName) return DrawingTestInput;
    if (sourceName === RemoteMicrophoneInputSource.inputName) return RemoteMicInput;
    return dummyInput;
  };
}

export default new InputManager();
